import React, { useState, useEffect } from "react";
import InnerLayout from "../../../layouts/inner-layout";
import { ChevronLeftIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { SquarePen, Trash2, Pause, Play, RefreshCw, ChevronDown, Download, Upload, Save } from "lucide-react";
import CSVUploadModal from "./csv-upload-modal";
import CSVPreviewModal from "./csv-preview-modal";
import Papa from "papaparse";
import ItemsSelectFieldStandalone from "../../../forms/stand-alone-select-field";
import excelImg from "../../../../assets/icons/excel.png";
import DownloadIcon from "../../../../assets/icons/download-icon";
import moment from "moment";
import { api } from "../../../../redux/apis/api";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import LoadingOverlay from "../../../helper-components/overlay-loading";

// Status Chip Component
const dummyOptions = {
    "TXN Initiated": [
        "Purchase_Order",
        "Pull_Orders",
        "Billing",
        "Customer_sync",
        "sales_order",
        "Purchase_Credit_Note",
        "Purchase_GRN",
        "Purchase_Invoice",
        "Purchase_Return",
        "Return_Billing",
        "Pull_Master_Data",
        "Price_List",
        "Coupon_Sync",
        "Scheme_Sync",
        "Loyalty_Config_Sync"
    ],
    "Sync Type": ["Intercompany", "sync"],
    "TXN reflected": [
        "sales_order",
        "Customer_sync",
        "Purchase_Credit_Note",
        "Purchase_GRN",
        "Purchase_Invoice",
        "Purchase_Return",
        "Return_Billing",
        "Pull_Master_Data",
        "Store_Information",
        "Coupon_Sync",
        "Scheme_Sync",
        "Loyalty_Config_Sync"
    ],
    "From POS": ["TPOS", "DPOS", "CPOS"],
    "To POS": ["Abhiram TPOS", "DPOS", "Warehouse A", "Warehouse B"],
    "Trigger": ["Schedule", "Real-time"],
    "Sync Interval Unit": ["Min", "Hr", "Day"],
};

// Map options to { label, value } format for ItemsSelectFieldStandalone
const mappedOptions = Object.keys(dummyOptions).reduce((acc, key) => {
    acc[key] = dummyOptions[key].map(opt => ({ label: opt, value: opt }));
    return acc;
}, {});

// Status Chip Component
const StatusChip = ({ status, type }) => {
    // Defaults
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-600";
    let borderColor = "border-gray-200";

    const lowerStatus = status?.toLowerCase();

    if (type === "status") {
        if (lowerStatus === "active") {
            bgColor = "bg-[var(--status-badge-green-bg)]";
            textColor = "text-[var(--status-badge-green-fg)]";
            borderColor = "border-[var(--status-badge-green-border)]";
        } else if (lowerStatus === "paused") {
            bgColor = "bg-[var(--trans-stats-con-orange-bg)]";
            textColor = "text-[var(--trans-stats-con-orange-fg)]";
            borderColor = "border-[var(--trans-stats-con-orange-fg)]";
        }
    } else if (type === "sync_status") {
        if (lowerStatus === "synced") {
            bgColor = "bg-[var(--status-badge-green-bg)]";
            textColor = "text-[var(--status-badge-green-fg)]";
            borderColor = "border-[var(--status-badge-green-border)]";
        } else if (lowerStatus === "in progress") {
            bgColor = "bg-[var(--status-badge-blue-bg)]";
            textColor = "text-[var(--status-badge-blue-fg)]";
            borderColor = "border-[var(--status-badge-blue-border)]";
        } else if (lowerStatus === "paused") {
            bgColor = "bg-gray-100";
            textColor = "text-gray-500";
            borderColor = "border-gray-200";
        }
    }

    return (
        <span
            className={`px-4 py-1.5 rounded-full text-[length:var(--status-badge-fs)] font-semibold border ${bgColor} ${textColor} ${borderColor}`}
        >
            {status}
        </span>
    );
};

// Progress Bar Component
const ProgressBar = ({ progress, status }) => {
    let colorClass = "bg-[var(--status-badge-green-border)]";
    if (status?.toLowerCase() === "in progress") {
        colorClass = "bg-[var(--status-badge-blue-border)]";
    } else if (status?.toLowerCase() === "paused") {
        colorClass = "bg-gray-400";
    }

    return (
        <div className="w-[120px]">
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1.5 overflow-hidden">
                <div
                    className={`${colorClass} h-1.5 rounded-full transition-all duration-500`}
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
            <div className="text-[10px] text-gray-500 font-medium">{progress}%</div>
        </div>
    );
};

const SyncRuleList = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [expandedRow, setExpandedRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Edit state
    const [editingRowId, setEditingRowId] = useState(null);

    // Preview Modal State
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [toPosOptions, setToPosOptions] = useState(mappedOptions["To POS"]);
    const [fromPosOptions, setFromPosOptions] = useState(mappedOptions["From POS"]);
    // Loading state from previous edit
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchSyncRules = async () => {
            setLoading(true);
            try {
                const response = await api.get(`/sync-rules/get`);
                const result = response.data?.data?.syncRules || [];
                const mappedData = result.map(item => ({
                    ...item,
                    id: item.sync_rule_id,
                    last_sync: item.last_sync_at ? moment(item.last_sync_at).format("YYYY-MM-DD, h:mm A") : "--",
                    next_sync: item.next_sync_at ? moment(item.next_sync_at).format("YYYY-MM-DD, h:mm A") : "--",
                }));
                setData(Array.isArray(mappedData) ? mappedData : []);
            } catch (error) {
                console.error("Error fetching sync rules:", error);
                errorToast("Failed to fetch sync rules");
            } finally {
                setLoading(false);
            }
        };

        const fetchToPosOptions = async () => {
            try {
                const response = await api.get(`/server-ips/get`);
                // According to network log, data is in response.data.data.serverIps
                const result = response.data?.data?.serverIps || response.data?.serverIps || [];
                const mapped = result.map(item => ({
                    label: item.server_name,
                    value: item.server_id // Use server_id as value for mapping
                }));
                if (mapped.length > 0) {
                    setToPosOptions(mapped);
                    setFromPosOptions(mapped); // Assuming From POS also uses same server list
                }
            } catch (error) {
                console.error("Error fetching to pos options:", error);
            }
        };

        fetchSyncRules();
        fetchToPosOptions();
    }, []);

    const handleDownloadTemplate = () => {
        const headers = [
            "Source POS",
            "Txn Source",
            "Sync Type",
            "Destination POS",
            "Txn Destination",
            "Trigger Type",
            "Sync Time",
            "Sync Interval Unit(Min / Hr / Day)"
        ];

        const csvContent = headers.join(",") + "\n";
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "sync_rule_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleUploadCSV = (file) => {
        if (file) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    // Map CSV headers to data keys
                    const mappedData = results.data.map(row => {
                        // Helper to find ID by Name
                        const findIdByName = (name, options) => {
                            if (!name || name === "Select") return "Select";
                            const option = options.find(opt => opt.label.toLowerCase() === name.toLowerCase());
                            return option ? option.value : name;
                        };

                        return {
                            from_pos: findIdByName(row["Source POS"], fromPosOptions),
                            txn_initiated: row["Txn Source"] || "Enter TXN Initiated",
                            sync_type: row["Sync Type"] || "Select",
                            to_pos: findIdByName(row["Destination POS"], toPosOptions),
                            txn_reflected: row["Txn Destination"] || "Select",
                            trigger: row["Trigger Type"] || "Select",
                            sync_interval_value: row["Sync Time"] || "",
                            sync_interval_unit: row["Sync Interval Unit(Min / Hr / Day)"] || "Select",
                            // Default fields for new rows
                            status: "Active",
                            sync_status: "--",
                            last_sync: "--",
                            next_sync: "Today, 2:30 PM",
                            sync_progress: 0,
                            id: Date.now() + Math.random(), // Ensure unique ID
                            isNew: true,
                        };
                    });

                    setPreviewData(mappedData);
                    setIsModalOpen(false); // Close upload modal
                    setIsPreviewModalOpen(true); // Open preview modal
                },
                error: (error) => {
                    console.error("Error parsing CSV:", error);
                    // Handle error (maybe show a toast)
                }
            });
        }
    };

    const handleConfirmUpload = () => {
        setData([...data, ...previewData]);
        setPreviewData([]);
        setIsPreviewModalOpen(false);
    };

    const handleCancelPreview = () => {
        setPreviewData([]);
        setIsPreviewModalOpen(false);
        setIsModalOpen(true); // Go back to upload modal
    };

    const toggleRow = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const handleAddRule = () => {
        const newRule = {
            txn_initiated: "Enter TXN Initiated",
            sync_type: "Select",
            txn_reflected: "Select",
            from_pos: "Select",
            to_pos: "Select",
            trigger: "Select",
            sync_interval_value: "",
            sync_interval_unit: "Select",
            status: "Active",
            sync_status: "--",
            last_sync: "--",
            next_sync: "Today, 2:30 PM",
            sync_progress: 0,
            id: Date.now(),
            isNew: true, // Flag for new rows
        };
        setData([...data, newRule]);
    };

    const handleDelete = async (id, isNew) => {
        if (isNew) {
            setData(data.filter((item) => item.id !== id));
        } else {
            setLoading(true);
            try {
                await api.post(`/sync-rule/delete`, { sync_rule_id: id });
                successToast("Sync Rule deleted successfully");
                setData(data.filter((item) => item.id !== id));
            } catch (error) {
                console.error("Error deleting sync rule:", error);
                errorToast(error.response?.data?.message || "Failed to delete sync rule");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCancel = () => {
        setData(data.filter((item) => !item.isNew));
        setEditingRowId(null);
    };

    const handleSave = async () => {
        const newRecords = data.filter(item => item.isNew);
        if (newRecords.length === 0) {
            setEditingRowId(null);
            return;
        }

        setLoading(true);

        const formatRecord = (item) => ({
            txn_initiated: item.txn_initiated,
            sync_type: item.sync_type,
            txn_reflected: item.txn_reflected,
            from_pos: item.from_pos,
            to_pos: item.to_pos,
            trigger: item.trigger,
            sync_interval_value: parseInt(item.sync_interval_value) || 0,
            sync_interval_unit: item.sync_interval_unit,
            status: "Active"
        });

        try {
            if (newRecords.length === 1) {
                // Use add API
                await api.post(`/sync-rule/add`, formatRecord(newRecords[0]));
                successToast("Sync Rule added successfully");
            } else {
                // Use import API
                await api.post(`/sync-rule/import`, newRecords.map(formatRecord));
                successToast("Sync Rules imported successfully");
            }

            setData(data.map(item => ({ ...item, isNew: false })));
            setEditingRowId(null);
        } catch (error) {
            console.error("Error saving sync rules:", error);
            errorToast(error.response?.data?.message || "Failed to save sync rules");
        } finally {
            setLoading(false);
        }
    };

    const isNewRecord = data.some((item) => item.isNew);
    const showFooter = isNewRecord;

    const handleFieldChange = (id, field, value) => {
        setData(data.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const toggleEdit = async (id) => {
        if (editingRowId === id) {
            // Save action for existing record
            const itemToUpdate = data.find(item => item.id === id);
            if (!itemToUpdate) return;

            setLoading(true);
            try {
                const payload = {
                    sync_rule_id: itemToUpdate.sync_rule_id || itemToUpdate.id,
                    txn_initiated: itemToUpdate.txn_initiated,
                    sync_type: itemToUpdate.sync_type,
                    txn_reflected: itemToUpdate.txn_reflected,
                    from_pos: itemToUpdate.from_pos,
                    to_pos: itemToUpdate.to_pos,
                    trigger: itemToUpdate.trigger,
                    sync_interval_value: parseInt(itemToUpdate.sync_interval_value) || 0,
                    sync_interval_unit: itemToUpdate.sync_interval_unit,
                };

                await api.post(`/sync-rule/update`, payload);
                successToast("Sync Rule updated successfully");
                setEditingRowId(null);
            } catch (error) {
                console.error("Error updating sync rule:", error);
                errorToast(error.response?.data?.message || "Failed to update sync rule");
            } finally {
                setLoading(false);
            }
        } else {
            // Edit action
            setEditingRowId(id);
        }
    };

    const handleSyncNow = (id) => {
        setData(data.map(item => {
            if (item.id === id) {
                return {
                    ...item,
                    sync_status: "In Progress",
                    sync_progress: 10,
                    last_sync: "Just now"
                };
            }
            return item;
        }));

        // Simulate progress completion
        setTimeout(() => {
            setData(prevData => prevData.map(item => {
                if (item.id === id) {
                    return { ...item, sync_status: "Synced", sync_progress: 100 };
                }
                return prevData === data ? item : item; // Safety check for state updates
            }));
        }, 2000);
    };

    const handlePauseResume = (id) => {
        setData(data.map(item => {
            if (item.id === id) {
                const newStatus = item.status === "Active" ? "Paused" : "Active";
                const newSyncStatus = item.status === "Active" ? "Paused" : "In Progress"; // Example logic
                return { ...item, status: newStatus, sync_status: newSyncStatus };
            }
            return item;
        }));
    };

    const tableHeading = [
        "TXN Initiated",
        "Sync Type",
        "TXN reflected",
        "From POS",
        "To POS",
        "Trigger",
        "Sync Interval",
        "Status",
        "Sync Status",
        "Last Sync",
        "Next Sync",
        "Sync Progress",
        "Sync Action",
        "Actions",
    ];

    return (
        <InnerLayout list={true} title="Sync Rule" module="sync-rule">
            <div className="flex flex-col h-full bg-white relative">
                <LoadingOverlay isLoading={loading} />
                <CSVUploadModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onUpload={handleUploadCSV}
                />

                <CSVPreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => setIsPreviewModalOpen(false)}
                    onConfirm={handleConfirmUpload}
                    onCancel={handleCancelPreview}
                    data={previewData}
                    toPosOptions={toPosOptions}
                    fromPosOptions={fromPosOptions}
                />
                {/* Header Breadcrumb & Add Button */}
                <div className="flex justify-between items-center pt-5 mx-6 mb-6">
                    <div className=" flex items-center gap-2 text-sml ">
                        <button
                            className="flex items-center gap-2"
                            onClick={() => {
                                navigate("/settings");
                            }}
                        >
                            <ChevronLeftIcon className="h-5 w-5 inline-block " />
                            System Settings /
                        </button>
                        <span className="cursor-pointer">{" " + "Sync Rule"}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleDownloadTemplate}
                            className="border-[var(--success-color)] border-2 rounded-md px-2 py-1 flex items-center justify-center hover:bg-green-50 transition-colors shadow-sm h-[38px] w-[38px]"
                            title="Download Template"
                        >
                            <DownloadIcon fill="var(--success-color)" />
                        </button>

                        <button
                            type="button"
                            className="border-[var(--success-color)] border-2 rounded-md flex items-center gap-2 text-nowrap px-4 py-1.5 cursor-pointer hover:bg-green-50 transition-colors shadow-sm h-[38px]"
                            onClick={() => setIsModalOpen(true)}
                        >
                            <img
                                width={18}
                                height={18}
                                src={excelImg}
                                alt="excel-icon"
                                className="object-contain"
                            />
                            <span className="text-[var(--success-color)] font-bold text-sm">Import</span>
                        </button>

                        <button
                            className="bg-[var(--primary-button-bg-color)] border border-[var(--primary-button-bg-color)] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors flex items-center gap-2 shadow-sm h-[38px]"
                            onClick={handleAddRule}
                        >
                            + Add Sync Rule
                        </button>
                    </div>
                </div>

                {/* Table Container */}
                <div className="mx-6 flex-1 overflow-hidden pb-4 flex flex-col">
                    <div className="overflow-auto rounded-xl border border-[var(--list-table-border-color)] bg-white flex-1 custom-scrollbar shadow-sm">
                        <table className="w-full text-left border-collapse min-w-max">
                            <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                                <tr className="relative h-[50px]">
                                    {tableHeading.map((heading) => (
                                        <th
                                            key={heading}
                                            className="px-5 py-3 text-[var(--list-table-heading-fg-color)] text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] uppercase tracking-wider whitespace-nowrap border-b border-[var(--list-table-border-color)]"
                                        >
                                            {heading}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--list-table-border-color)]">
                                {data.map((item, index) => {
                                    const isEditing = editingRowId === item.id || item.isNew;
                                    return (
                                        <React.Fragment key={index}>
                                            <tr className={`transition-all duration-200 h-[56px] group ${isEditing ? "bg-[var(--status-badge-blue-bg)] hover:bg-blue-100 shadow-[inset_4px_0_0_0_#277dfe]" : "bg-[var(--list-table-row-bg)] hover:bg-[var(--list-table-row-hover-bg)]"}`}>
                                                <td className="px-5 py-3 whitespace-nowrap min-w-[150px] border-b border-[var(--list-table-border-color)]">
                                                    <ItemsSelectFieldStandalone
                                                        value={item.txn_initiated}
                                                        options={mappedOptions["TXN Initiated"]}
                                                        onChange={(val) => handleFieldChange(item.id, "txn_initiated", val.value)}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isDisabled={!isEditing}
                                                        placeholder="Select TXN Initiated"
                                                    />
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap min-w-[150px] border-b border-[var(--list-table-border-color)]">
                                                    <ItemsSelectFieldStandalone
                                                        value={item.sync_type}
                                                        options={mappedOptions["Sync Type"]}
                                                        onChange={(val) => handleFieldChange(item.id, "sync_type", val.value)}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isDisabled={!isEditing}
                                                        placeholder="Select Sync Type"
                                                    />
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap min-w-[150px] border-b border-[var(--list-table-border-color)]">
                                                    <ItemsSelectFieldStandalone
                                                        value={item.txn_reflected}
                                                        options={mappedOptions["TXN reflected"]}
                                                        onChange={(val) => handleFieldChange(item.id, "txn_reflected", val.value)}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isDisabled={!isEditing}
                                                        placeholder="Select TXN reflected"
                                                    />
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap min-w-[120px] border-b border-[var(--list-table-border-color)]">
                                                    <ItemsSelectFieldStandalone
                                                        value={item.from_pos}
                                                        options={mappedOptions["From POS"]}
                                                        onChange={(val) => handleFieldChange(item.id, "from_pos", val.value)}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isDisabled={!isEditing}
                                                        placeholder="Select Source"
                                                    />
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap min-w-[150px] border-b border-[var(--list-table-border-color)]">
                                                    <ItemsSelectFieldStandalone
                                                        value={item.to_pos}
                                                        options={toPosOptions}
                                                        onChange={(val) => handleFieldChange(item.id, "to_pos", val.value)}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isDisabled={!isEditing}
                                                        placeholder="Select Destination"
                                                    />
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap min-w-[120px] border-b border-[var(--list-table-border-color)]">
                                                    <ItemsSelectFieldStandalone
                                                        value={item.trigger}
                                                        options={mappedOptions["Trigger"]}
                                                        onChange={(val) => handleFieldChange(item.id, "trigger", val.value)}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isDisabled={!isEditing}
                                                        placeholder="Select Trigger"
                                                    />
                                                </td>

                                                {/* Sync Interval Column */}
                                                <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <div className="flex flex-col w-[50px]">
                                                            <input
                                                                type="text"
                                                                defaultValue={item.sync_interval_value}
                                                                className={`w-full border rounded-md h-[32px] px-2 text-center bg-white focus:outline-none font-medium placeholder-gray-300 ${isEditing ? "border-[var(--stand-alone-input-border)] focus:border-[var(--input-focus)] text-[var(--input-focus)]" : "border-transparent text-gray-700 bg-transparent"}`}
                                                                placeholder="Val"
                                                                onChange={(e) => handleFieldChange(item.id, "sync_interval_value", e.target.value)}
                                                                readOnly={!isEditing}
                                                            />
                                                        </div>
                                                        <div className="w-[80px]">
                                                            <ItemsSelectFieldStandalone
                                                                value={item.sync_interval_unit}
                                                                options={mappedOptions["Sync Interval Unit"]}
                                                                onChange={(val) => handleFieldChange(item.id, "sync_interval_unit", val.value)}
                                                                isSearchable={true}
                                                                isCreatable={true}
                                                                isDisabled={!isEditing}
                                                                placeholder="Unit"
                                                            />
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                    <StatusChip status={item.status} type="status" />
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                    {item.sync_status === "--" ? (
                                                        <span className="text-gray-500 text-[11px] font-medium text-center block w-full">--</span>
                                                    ) : (
                                                        <StatusChip
                                                            status={item.sync_status}
                                                            type="sync_status"
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-[12px] text-gray-600 whitespace-nowrap font-medium border-b border-[var(--list-table-border-color)]">
                                                    {item.last_sync}
                                                </td>
                                                <td className="px-5 py-3 text-[12px] text-gray-600 whitespace-nowrap font-medium border-b border-[var(--list-table-border-color)]">
                                                    {item.next_sync}
                                                </td>
                                                <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                    <ProgressBar
                                                        progress={item.sync_progress}
                                                        status={item.sync_status}
                                                    />
                                                </td>

                                                {/* Sync Action Buttons */}
                                                <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                    {(item.sync_status?.toLowerCase() === "synced" || item.sync_status === "--") && (
                                                        <button
                                                            className="bg-[var(--primary-button-bg-color)] text-white px-4 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 hover:bg-blue-600 shadow-sm transition-all active:scale-95"
                                                            onClick={() => handleSyncNow(item.id)}
                                                        >
                                                            <RefreshCw size={12} strokeWidth={2.5} />
                                                            Sync Now
                                                        </button>
                                                    )}
                                                    {item.sync_status?.toLowerCase() === "in progress" && (
                                                        <span className="text-gray-500 text-[11px] font-medium flex items-center gap-1.5 px-1">
                                                            <RefreshCw size={12} className="animate-spin text-[var(--primary-button-bg-color)]" />
                                                            Syncing...
                                                        </span>
                                                    )}
                                                    {item.sync_status?.toLowerCase() === "paused" && (
                                                        <button className="bg-[var(--trans-stats-con-orange-bg)] text-[var(--trans-stats-con-orange-fg)] border border-[var(--trans-stats-con-orange-fg)] px-4 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 shadow-sm opacity-80 cursor-not-allowed">
                                                            <Pause size={12} fill="currentColor" strokeWidth={0} />
                                                            Paused
                                                        </button>
                                                    )}
                                                </td>

                                                {/* Action Icons */}
                                                <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            className={`p-1.5 rounded transition-colors ${isEditing ? "text-[#277dfe] bg-[#277dfe] bg-opacity-10" : "text-gray-400 hover:text-[#277dfe] hover:bg-[#277dfe] hover:bg-opacity-10"}`}
                                                            title={isEditing ? "Save" : "Edit"}
                                                            onClick={() => (item.isNew ? handleSave() : toggleEdit(item.id))}
                                                        >
                                                            {isEditing ? <Save size={15} strokeWidth={2.5} /> : <SquarePen size={15} strokeWidth={2.5} />}
                                                        </button>
                                                        <div className="w-[1px] h-4 bg-gray-300 mx-1"></div>

                                                        {item.status === "Paused" ? (
                                                            <button
                                                                title="Resume"
                                                                className="p-1.5 text-[var(--primary-button-bg-color)] hover:bg-blue-50 rounded transition-colors"
                                                                onClick={() => handlePauseResume(item.id)}
                                                            >
                                                                <Play size={15} fill="currentColor" strokeWidth={0} />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                title="Pause"
                                                                className="p-1.5 text-gray-400 hover:text-[var(--primary-button-bg-color)] hover:bg-blue-50 rounded transition-colors"
                                                                onClick={() => handlePauseResume(item.id)}
                                                            >
                                                                <Pause size={15} fill="currentColor" strokeWidth={0} />
                                                            </button>
                                                        )}

                                                        <button
                                                            className="p-1.5 text-[var(--remove-color)] hover:bg-red-50 rounded transition-colors"
                                                            title="Delete"
                                                            onClick={() => handleDelete(item.id, item.isNew)}
                                                        >
                                                            <Trash2 size={15} strokeWidth={2.5} />
                                                        </button>
                                                        <button
                                                            title="More"
                                                            className={`ml-1 p-1 bg-[var(--status-badge-blue-bg)] text-[var(--primary-button-bg-color)] hover:bg-blue-100 rounded-md transition-all duration-200 ${expandedRow === index ? 'rotate-180' : ''}`}
                                                            onClick={() => toggleRow(index)}
                                                        >
                                                            <ChevronDown size={14} strokeWidth={3} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {/* Notes Section Row */}
                                            {expandedRow === index && (
                                                <tr>
                                                    <td colSpan={tableHeading.length} className="px-5 py-4 bg-white border-b border-gray-200">
                                                        <div className="pl-4">
                                                            <label className="block text-xs font-bold text-gray-900 mb-2">Notes</label>
                                                            <textarea
                                                                className="w-full max-w-[600px] border border-gray-200 rounded-md p-3 text-xs text-gray-600 focus:outline-none focus:border-blue-400 resize-none h-[80px]"
                                                                placeholder="Syncs GRN updates to Sales Invoices for intercompany billing."
                                                            >
                                                            </textarea>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {showFooter && (
                        <div className="flex justify-end items-center gap-3 pt-4 border-t border-[var(--list-table-border-color)] mt-auto bg-white">
                            <button
                                className="px-6 py-2 border border-[var(--primary-button-bg-color)] text-[var(--primary-button-bg-color)] rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-6 py-2 border border-[var(--primary-button-bg-color)] text-[var(--primary-button-bg-color)] rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
                            >
                                Draft
                            </button>
                            <button
                                className="px-8 py-2 bg-[var(--primary-button-bg-color)] text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors shadow-sm"
                                onClick={handleSave}
                            >
                                Save
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </InnerLayout>
    );
};

export default SyncRuleList;
